import {computed, inject, Injectable, PLATFORM_ID, Signal, signal} from '@angular/core';
import {GroupVoucherCode, Mutation,} from '../interfaces/generated/graphql';
import {BehaviorSubject, Observable} from 'rxjs';


import {Router} from '@angular/router';
import {RoutingService} from './routing.service';
import {isPlatformServer} from '@angular/common';
import {ApolloService} from './apollo.service';
import {CartService} from './cart.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CustomOneProductFilter} from '@core/services/algolia.service';
import {environment} from '@env/environment';

export type GroupVoucherWrapper = { groupVoucher: GroupVoucherCode | null, loading: boolean, isCustomerLinked: boolean };

@Injectable({
  providedIn: 'root'
})
export class GroupVoucherService {
  private apollo = inject(ApolloService);
  private router = inject(Router);
  private routingService = inject(RoutingService);
  private cartService = inject(CartService);
  private modalService = inject(NgbModal);
  protected platformId = inject(PLATFORM_ID);


  private groupVoucher: BehaviorSubject<GroupVoucherCode | null> = new BehaviorSubject<GroupVoucherCode | null>(null);
  voucherSearchPageFilter = computed(() => {
    if (isPlatformServer(this.platformId)) {
      return {
        types: [],
        objectIds: [],
        title: '',
        storyblok: undefined
      };
    }
    const groupVoucher = this.groupVoucherSignal();
    const filter = groupVoucher.groupVoucher?.prices?.map((price, index) => ({
      vismaId: price?.vismaId,
      weight: index,
    })) as CustomOneProductFilter[];
    return {
      types: [],
      objectIds: filter,
      title: 'Voucher',
      storyblok: undefined
    };
  });

  constructor() {
    this.getGroupVoucherFromUrl();
  }

  protected _groupVoucherSignal = signal<GroupVoucherWrapper>({groupVoucher: null, loading: true, isCustomerLinked: false})

  currentGroupVoucher(): GroupVoucherCode | null {
    return this.groupVoucher.getValue();
  }

  async getGroupVoucherFromUrl() {
    if (environment.supervin.hideGroupVoucher) {
      return;
    }
    const query = this.routingService.getQueryParams();
    const applyGroupVoucher = query['applyCode'];
    if (applyGroupVoucher) {
      const groupVoucher = await this.applyGroupVoucher(applyGroupVoucher);
      const useRedirectTo = query['useRedirect'] === 'true';
      if (useRedirectTo) {
        this.router.navigateByUrl(groupVoucher?.redirectTo || '/voucher/list');
      }
    } else {
      this.runOldGroupVoucherParamLogic(query);
    }
  }

  /** @deprecated Use instead applyGroupVoucher param */
  async runOldGroupVoucherParamLogic(query: any) {
    const voucher = query['voucher'];
    if (voucher) {
      const voucherData = await this.applyGroupVoucher(voucher);
      this.router.navigateByUrl(voucherData?.redirectTo || '/voucher/list');
    }
  }

  get groupVoucherSignal(): Signal<GroupVoucherWrapper> {
    return this._groupVoucherSignal.asReadonly();
  }

  setGroupVoucherLoading(loading: boolean) {
    this._groupVoucherSignal.update(current => ({...current, loading}));
  }

  getGroupVoucher(): Observable<GroupVoucherCode | null> {
    return this.groupVoucher.asObservable()
  }

  setGroupVoucher(groupVoucher: GroupVoucherCode | null, isCustomerLinked = false) {
    this.groupVoucher.next(groupVoucher);
    this._groupVoucherSignal.set({groupVoucher, loading: false, isCustomerLinked});
  }

  async applyGroupVoucher(code: string): Promise<GroupVoucherCode | undefined> {
    this.setGroupVoucherLoading(true);
    const res = await this.apollo.mutatePromise<Mutation>({
      queryName: 'applyGroupVoucherMutation',
      variables: {
        code
      }
    }).catch(err => {
      const graphQLError = err.graphQLErrors?.[0];
      const groupVoucherCompanyProfileError = graphQLError.debugMessage === 'GROUP_VOUCHER_COMPANY_PROFILE_EXPIRED_OR_INVALID';
      if (groupVoucherCompanyProfileError && graphQLError.context.redirect) {
        this.router.navigateByUrl(graphQLError.context.redirect);
        this.modalService.dismissAll();
        return;
      }
      throw err;
    })

    const voucher = res?.data?.applyGroupVoucher;
    if (voucher?.groupVoucher?.code) {
      this.setGroupVoucher(voucher.groupVoucher);
      this.cartService.updateCart(voucher.cart)
    } else {
      this.setGroupVoucher(null);
    }
    return voucher?.groupVoucher || undefined;
  }

  removeGroupVoucher() {
    return this.apollo.mutatePromise({
      queryName: 'removeGroupVoucherFromCartMutation'
    }).then(res => {
      this.setGroupVoucher(null);
      return res;
    });
  }
}
